import { useSeoMeta } from '@unhead/react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import {
  Terminal,
  Package,
  Server,
  ExternalLink,
  Zap,
  ArrowLeft,
  GitBranch,
  Globe,
  Scale,
  Calendar,
  Pencil,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { LoginArea } from '@/components/auth/LoginArea';
import { useSkill } from '@/hooks/useSkills';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { formatPrice, generateInstallCommand } from '@/lib/skills';
import { genUserName } from '@/lib/genUserName';
import { TrustScore } from '@/components/TrustScore';
import { SkillReviews } from '@/components/SkillReviews';
import { MarkdownContent } from '@/components/MarkdownContent';


export default function SkillDetail() {
  const { pubkey, identifier } = useParams<{ pubkey: string; identifier: string }>();
  const { data: skill, isLoading, error } = useSkill(identifier || '', pubkey);
  const { user } = useCurrentUser();

  useSeoMeta({
    title: skill ? `${skill.name} - SkillForge` : 'Skill - SkillForge',
    description: skill?.description || 'View skill details on SkillForge.',
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <SkillDetailHeader />
        <main className="container mx-auto px-4 py-8">
          <SkillDetailSkeleton />
        </main>
      </div>
    );
  }

  if (error || !skill) {
    return (
      <div className="min-h-screen bg-background">
        <SkillDetailHeader />
        <main className="container mx-auto px-4 py-20">
          <div className="max-w-md mx-auto text-center">
            <Package className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <h1 className="text-2xl font-bold mb-2">Skill not found</h1>
            <p className="text-muted-foreground mb-6">
              This skill may have been removed or the link is incorrect.
            </p>
            <Link to="/browse">
              <Button variant="outline" className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                Back to Browse
              </Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SkillDetailHeader />

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Link to="/browse" className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1">
              <ArrowLeft className="w-3 h-3" />
              Back to Browse
            </Link>
          </div>

          {/* Header */}
          <div className="flex flex-col md:flex-row gap-6 mb-10">
            <div className={`w-20 h-20 shrink-0 rounded-2xl flex items-center justify-center border border-white/[0.08] ${skill.type === 'mcp' ? 'bg-gradient-to-br from-emerald-500/20 to-green-500/20 text-emerald-400' : 'bg-gradient-to-br from-orange-500/20 to-amber-500/20 text-orange-400'} glow-primary-sm`}>
              {skill.type === 'mcp' ? <Server className="w-10 h-10" /> : <Package className="w-10 h-10" />}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h1 className="text-3xl font-bold mb-3">{skill.name}</h1>
                  <AuthorBadge pubkey={skill.pubkey} />
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant={skill.price > 0 ? 'default' : 'secondary'} className={`text-base px-4 py-1.5 ${skill.price > 0 ? 'bg-gradient-to-r from-orange-500/90 to-amber-500/90 border-0 shadow-lg shadow-orange-500/20' : 'bg-white/[0.05] border-white/[0.08]'}`}>
                    {skill.price > 0 && <Zap className="w-4 h-4 mr-1" />}
                    {formatPrice(skill.price)}
                  </Badge>
                  {user?.pubkey === skill.pubkey && (
                    <Link to={`/edit/${skill.identifier}`}>
                      <Button size="sm" variant="outline" className="gap-1.5 border-white/[0.1] hover:border-orange-500/40 hover:text-orange-400 transition-colors">
                        <Pencil className="w-3.5 h-3.5" />
                        Edit
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Content Grid */}
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Description */}
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle>Description</CardTitle>
                </CardHeader>
                <CardContent>
                  <MarkdownContent content={skill.description} />

                  {skill.tags.length > 0 && (
                    <TagList tags={skill.tags} />
                  )}
                </CardContent>
              </Card>

              {/* Installation */}
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle>Installation</CardTitle>
                </CardHeader>
                <CardContent>
                  <MarkdownContent content={generateInstallCommand(skill)} />
                </CardContent>
              </Card>

              {/* Pricing Info (if paid) */}
              {skill.price > 0 && (
                <Card className="glass-card border-amber-500/20 bg-gradient-to-br from-amber-500/[0.08] via-orange-500/[0.04] to-transparent">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Zap className="w-5 h-5 text-amber-400" />
                      Paid Skill
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-muted-foreground">
                      This {skill.type === 'skill' ? 'skill' : 'MCP server'} costs{' '}
                      <strong className="text-foreground">{formatPrice(skill.price)}</strong> per use,
                      enforced via a NIP-90 DVM server run by the publisher.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Agents must send a kind 5900 job request and pay the Lightning invoice before receiving results.
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Reviews Section */}
              <SkillReviews skill={skill} />
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Web of Trust */}
              <TrustScore skill={skill} />

              {/* Meta Info */}
              <Card className="glass-card">
                <CardContent className="pt-6 space-y-4">
                  <div className="flex items-center gap-3 text-sm">
                    <GitBranch className="w-4 h-4 text-orange-400/70" />
                    <span className="text-muted-foreground">Version</span>
                    <span className="ml-auto font-mono">{skill.version}</span>
                  </div>

                  {skill.license && (
                    <div className="flex items-center gap-3 text-sm">
                      <Scale className="w-4 h-4 text-orange-400/70" />
                      <span className="text-muted-foreground">License</span>
                      <span className="ml-auto">{skill.license}</span>
                    </div>
                  )}

                  <div className="flex items-center gap-3 text-sm">
                    <Calendar className="w-4 h-4 text-orange-400/70" />
                    <span className="text-muted-foreground">Published</span>
                    <span className="ml-auto">
                      {new Date(skill.createdAt * 1000).toLocaleDateString()}
                    </span>
                  </div>

                  {skill.repository && (
                    <a
                      href={skill.repository}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 text-sm hover:text-orange-400 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4 text-orange-400/70" />
                      <span>Repository</span>
                      <ExternalLink className="w-3 h-3 ml-auto" />
                    </a>
                  )}

                  {skill.homepage && (
                    <a
                      href={skill.homepage}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 text-sm hover:text-orange-400 transition-colors"
                    >
                      <Globe className="w-4 h-4 text-orange-400/70" />
                      <span>Homepage</span>
                      <ExternalLink className="w-3 h-3 ml-auto" />
                    </a>
                  )}
                </CardContent>
              </Card>

              {/* Type Badge */}
              <Card className="glass-card">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-2">
                    {skill.type === 'mcp' ? (
                      <Server className="w-5 h-5 text-emerald-400" />
                    ) : (
                      <Package className="w-5 h-5 text-orange-400" />
                    )}
                    <span className="font-semibold">
                      {skill.type === 'mcp' ? 'MCP Server' : 'OpenClaw Skill'}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {skill.type === 'mcp'
                      ? 'Model Context Protocol server for AI assistants'
                      : 'Autonomous capability for AI agents'}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function SkillDetailHeader() {
  const { user } = useCurrentUser();

  return (
    <header className="sticky top-0 z-50 backdrop-blur-2xl bg-background/60 border-b border-white/[0.06]">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500/20 to-emerald-500/20 flex items-center justify-center glow-primary-sm border border-white/[0.08] group-hover:border-white/[0.15] transition-all">
            <Terminal className="w-4 h-4 text-orange-400" />
          </div>
          <span className="font-semibold text-lg tracking-tight">SkillForge</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
          <Link to="/browse" className="hover:text-foreground transition-colors">Browse</Link>
          <Link to="/my-skills" className="hover:text-foreground transition-colors">My Skills</Link>
          <Link to="/connect-agent" className="hover:text-foreground transition-colors">Connect Agent</Link>
          <Link to="/publish" className="hover:text-foreground transition-colors opacity-50 hover:opacity-100 text-xs">Publish</Link>
        </nav>

        <div className="flex items-center gap-3">
          <LoginArea className="max-w-60" />
        </div>
      </div>
    </header>
  );
}

function TagList({ tags }: { tags: string[] }) {
  const navigate = useNavigate();

  return (
    <div className="flex flex-wrap gap-2 mt-5 pt-5 border-t border-white/[0.06]">
      {tags.map((tag) => (
        <Badge
          key={tag}
          variant="outline"
          onClick={() => navigate(`/browse?tag=${encodeURIComponent(tag)}`)}
          className="bg-white/[0.02] border-white/[0.08] cursor-pointer hover:bg-orange-500/10 hover:border-orange-500/30 hover:text-orange-400 transition-colors"
        >
          #{tag}
        </Badge>
      ))}
    </div>
  );
}

function AuthorBadge({ pubkey }: { pubkey: string }) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const name = metadata?.name || genUserName(pubkey);
  const npub = nip19.npubEncode(pubkey);

  return (
    <Link to={`/profile/${npub}`} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
      <Avatar className="w-6 h-6">
        <AvatarImage src={metadata?.picture} alt={name} />
        <AvatarFallback className="text-xs">{name[0]?.toUpperCase()}</AvatarFallback>
      </Avatar>
      <span className="text-sm text-muted-foreground">
        by <span className="text-foreground hover:text-primary transition-colors">{name}</span>
      </span>
    </Link>
  );
}


function SkillDetailSkeleton() {
  return (
    <div className="max-w-4xl mx-auto">
      <Skeleton className="h-4 w-24 mb-6" />
      <div className="flex gap-6 mb-8">
        <Skeleton className="w-20 h-20 rounded-2xl" />
        <div className="flex-1 space-y-3">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <Skeleton className="h-48 rounded-xl" />
          <Skeleton className="h-32 rounded-xl" />
        </div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    </div>
  );
}
